import { useState } from "react";
import {
  Youtube,
  Sparkles,
  PlayCircle,
  Compass,
  Brain,
  Globe2,
  LineChart,
  Wallet,
  TrendingUp,
  Layers,
  Radio,
  ShieldAlert,
  Search,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

type VideoCategory = "Format" | "Framework" | "Documentary" | "News";

type Video = {
  id: string;
  title: string;
  category: VideoCategory;
  duration: string;
  videoSrc: string;
  posterSrc?: string;
  topic?: string;
  meta?: { label: string; value: string }[];
  notes?: string;
};

const VIDEOS: Video[] = [
  // Featured — latest, hybrid format
  {
    id: "transformer-doc",
    title: "How an LLM reads your prompt — the transformer, explained",
    category: "Documentary",
    duration: "5:02",
    videoSrc: "/youtube-lab/transformer-documentary.mp4",
    posterSrc: "/youtube-lab/transformer-documentary-poster.jpg",
    topic: "Tokenization → embeddings → attention → transformer stack → sampling. Hybrid: live B-roll + custom motion graphics with real Vaswani equation, real attention heatmap, real model specs.",
    meta: [
      { label: "Format", value: "Hybrid (63% motion / 37% live)" },
      { label: "Voice", value: "gpt-4o-mini-tts onyx + delivery instructions" },
      { label: "Motion graphics", value: "6 custom scenes" },
      { label: "B-roll", value: "6 Pexels clips · all unique" },
      { label: "Lower-thirds", value: "Definitions + spec callouts" },
      { label: "Cost", value: "~$0.05 (TTS only)" },
    ],
    notes: "Iteration 2 — fixed all 3 SVB feedback points: zero B-roll repetition (vs 39 clips with money loops), real motion graphics with attention heatmap + Vaswani equation, voice with delivery instructions for character. ~720 words narration, 5 min.",
  },
  // SVB documentary
  {
    id: "svb-doc",
    title: "SVB Collapse — real documentary cut",
    category: "Documentary",
    duration: "6:48",
    videoSrc: "/youtube-lab/svb-documentary.mp4",
    posterSrc: "/youtube-lab/svb-documentary-poster.jpg",
    topic: "What killed Silicon Valley Bank in 36 hours — duration risk, uninsured deposits, Twitter as accelerant.",
    meta: [
      { label: "B-roll", value: "39 Pexels clips · 59 cuts" },
      { label: "Lower-thirds", value: "47 timecoded callouts" },
      { label: "Avg cut", value: "~7s per cut" },
      { label: "Music", value: "ffmpeg-synthesized drone" },
      { label: "Loudness", value: "-16 LUFS YouTube" },
      { label: "Cost", value: "~$0 marginal" },
    ],
    notes: "Reused script + voice from News-03. Iteration 1 documentary — real B-roll under lower-thirds. Critique: too much money imagery, B-roll repetition, no motion graphics. Fixed in transformer doc above.",
  },
  // News Lab — 10 videos
  {
    id: "news-01",
    title: "Why the dollar's reserve-currency status is being challenged",
    category: "News",
    duration: "7:45",
    videoSrc: "/youtube-lab/news-01.mp4",
    posterSrc: "/youtube-lab/news-01-poster.jpg",
    topic: "BRICS de-dollarization · central-bank gold buying · Saudi yuan oil · sanctions weaponization",
    meta: [{ label: "File size", value: "14.9 MB" }, { label: "Format", value: "Bloomberg Terminal kinetic typography" }],
  },
  {
    id: "news-02",
    title: "Inside a Transformer — how an LLM generates each token",
    category: "News",
    duration: "6:39",
    videoSrc: "/youtube-lab/news-02.mp4",
    posterSrc: "/youtube-lab/news-02-poster.jpg",
    topic: "Tokenization · embeddings · attention · sampling · the mechanism, not the metaphor",
    meta: [{ label: "File size", value: "12.4 MB" }],
  },
  {
    id: "news-03",
    title: "What killed Silicon Valley Bank in 36 hours",
    category: "News",
    duration: "6:48",
    videoSrc: "/youtube-lab/news-03.mp4",
    posterSrc: "/youtube-lab/news-03-poster.jpg",
    topic: "Bank-run mechanics · duration risk · uninsured deposits · Twitter as accelerant",
    meta: [{ label: "File size", value: "13.0 MB" }, { label: "Note", value: "Documentary version above" }],
  },
  {
    id: "news-04",
    title: "China's mortgage crisis — bigger than 2008?",
    category: "News",
    duration: "5:54",
    videoSrc: "/youtube-lab/news-04.mp4",
    posterSrc: "/youtube-lab/news-04-poster.jpg",
    topic: "Pre-sale system · Evergrande · LGFV debt · demographic collapse",
    meta: [{ label: "File size", value: "9.8 MB" }],
  },
  {
    id: "news-05",
    title: "Why every AI startup just rebuilt itself around agents",
    category: "News",
    duration: "5:58",
    videoSrc: "/youtube-lab/news-05.mp4",
    posterSrc: "/youtube-lab/news-05-poster.jpg",
    topic: "ReAct loop · tool use · MCP · 2025 inflection · Devin · Cursor · Skills",
    meta: [{ label: "File size", value: "10.5 MB" }],
  },
  {
    id: "news-06",
    title: "Interest rates: the only economic weapon that matters",
    category: "News",
    duration: "5:27",
    videoSrc: "/youtube-lab/news-06.mp4",
    posterSrc: "/youtube-lab/news-06-poster.jpg",
    topic: "Fed Funds → Treasury → mortgage → equity → FX · transmission mechanism",
    meta: [{ label: "File size", value: "19.1 MB" }],
  },
  {
    id: "news-07",
    title: "How TSMC quietly became the most important company on Earth",
    category: "News",
    duration: "6:13",
    videoSrc: "/youtube-lab/news-07.mp4",
    posterSrc: "/youtube-lab/news-07-poster.jpg",
    topic: "Foundry model · ASML EUV chokepoint · Taiwan strait · CHIPS Act",
    meta: [{ label: "File size", value: "14.2 MB" }],
  },
  {
    id: "news-08",
    title: "MCP — why every AI tool just standardized on the same protocol",
    category: "News",
    duration: "6:48",
    videoSrc: "/youtube-lab/news-08.mp4",
    posterSrc: "/youtube-lab/news-08-poster.jpg",
    topic: "Model Context Protocol · 17K+ servers · USB-C / HTTP / LSP analogy",
    meta: [{ label: "File size", value: "11.3 MB" }],
  },
  {
    id: "news-09",
    title: "Gold at $4,728 — what the market is actually saying",
    category: "News",
    duration: "5:33",
    videoSrc: "/youtube-lab/news-09.mp4",
    posterSrc: "/youtube-lab/news-09-poster.jpg",
    topic: "Central-bank tonnage · $300B Russia freeze · sovereign debt · the broken correlation",
    meta: [{ label: "File size", value: "21.8 MB" }],
  },
  {
    id: "news-10",
    title: "AI training economics — why a frontier model costs $200M",
    category: "News",
    duration: "5:07",
    videoSrc: "/youtube-lab/news-10.mp4",
    posterSrc: "/youtube-lab/news-10-poster.jpg",
    topic: "Cost stack · scaling laws · GPT-5 vs DeepSeek anomaly",
    meta: [{ label: "File size", value: "9.5 MB" }],
  },
  // Format demos
  {
    id: "format-1",
    title: "Format demo — Animated Explainer",
    category: "Format",
    duration: "0:20",
    videoSrc: "/youtube-lab/format-1.mp4",
    topic: "How an AI agent thinks in 5 steps · pure Remotion motion graphics + AI narration",
    meta: [{ label: "Cost", value: "$0.008 (TTS only)" }, { label: "Render", value: "~30s local CPU" }],
  },
  {
    id: "format-2",
    title: "Format demo — Faceless Documentary",
    category: "Format",
    duration: "0:21",
    videoSrc: "/youtube-lab/format-2.mp4",
    topic: "China mortgage crisis in 20 seconds · Pexels stock + lower-thirds + journalist narration",
    meta: [{ label: "Cost", value: "$0.01" }, { label: "Loudness", value: "-16 LUFS" }],
  },
  {
    id: "format-3",
    title: "Format demo — Atmospheric Cinematic",
    category: "Format",
    duration: "0:20",
    videoSrc: "/youtube-lab/format-3.mp4",
    topic: "What changed the day AGI arrived · Veo 3.0 generative clips + Herzog narration",
    meta: [{ label: "Cost", value: "~$3.60 (Veo)" }, { label: "Note", value: "AI video generation" }],
  },
  // Framework demos
  {
    id: "fw-hyperframes-1",
    title: "Hyperframes — meta explainer",
    category: "Framework",
    duration: "0:20",
    videoSrc: "/youtube-lab/fw-hyperframes-1.mp4",
    topic: "What is Hyperframes? · pipeline diagram + kinetic typography",
    meta: [{ label: "Cost", value: "$0.009" }, { label: "Best for", value: "AI agents · single HTML file model" }],
  },
  {
    id: "fw-hyperframes-2",
    title: "Hyperframes — Bloomberg finance",
    category: "Framework",
    duration: "0:20",
    videoSrc: "/youtube-lab/fw-hyperframes-2.mp4",
    topic: "Inflation in 20 seconds · Bloomberg Terminal aesthetic with SVG path animation",
    meta: [{ label: "Cost", value: "$0.010" }],
  },
  {
    id: "fw-hyperframes-3",
    title: "Hyperframes — atmospheric",
    category: "Framework",
    duration: "0:20",
    videoSrc: "/youtube-lab/fw-hyperframes-3.mp4",
    topic: "Starlight · stylized cinematic with no AI video generation · ~330× cheaper than Veo",
    meta: [{ label: "Cost", value: "$0.011" }],
  },
  {
    id: "fw-motion-canvas",
    title: "Motion Canvas — educational",
    category: "Framework",
    duration: "0:20",
    videoSrc: "/youtube-lab/fw-motion-canvas.mp4",
    topic: "Why circles are everywhere in math · generator-based timeline `yield* sequence(...)`",
    meta: [{ label: "Cost", value: "$0.010" }, { label: "Best for", value: "Choreographed timing" }],
  },
  {
    id: "fw-manim",
    title: "Manim — algorithmic",
    category: "Framework",
    duration: "0:20",
    videoSrc: "/youtube-lab/fw-manim.mp4",
    topic: "Bubble sort visualized · 3Blue1Brown aesthetic in Python",
    meta: [{ label: "Cost", value: "$0.010" }, { label: "Best for", value: "Math + LaTeX" }],
  },
  {
    id: "fw-remotion",
    title: "Remotion — data-driven",
    category: "Framework",
    duration: "0:20",
    videoSrc: "/youtube-lab/fw-remotion.mp4",
    topic: "AI video model cost rankings · animated bar chart from JSON",
    meta: [{ label: "Cost", value: "$0.011" }, { label: "Best for", value: "React component reuse · scale" }],
  },
];

type NicheCandidate = {
  id: string;
  name: string;
  icon: typeof Brain;
  iconColor: string;
  thesis: string;
  why: string;
  rpm: string;
  topChannels: string[];
  risk: string;
};

const NICHES: NicheCandidate[] = [
  {
    id: "finance",
    name: "Finance & business",
    icon: LineChart,
    iconColor: "text-emerald-500",
    thesis: "Demystify how money actually works — analytical, not 'get rich'",
    why: "Highest RPM in the entire YouTube ecosystem. How Money Works (1.7M subs, ~$57k/mo) is the direct Claude-loop template.",
    rpm: "$10–25 / 1k views",
    topChannels: ["How Money Works", "Patrick Boyle", "Logically Answered", "Company Man", "The Plain Bagel"],
    risk: "'Get rich' framing demonetizes — stay analytical, audience expects rigor.",
  },
  {
    id: "world",
    name: "Make sense of the world",
    icon: Globe2,
    iconColor: "text-rose-500",
    thesis: "First-principles geopolitics + current events",
    why: "Massive view ceilings, faceless-friendly, evergreen. RealLifeLore: 7.9M subs, $14k+/mo from AdSense alone.",
    rpm: "$4–8 / 1k views",
    topChannels: ["RealLifeLore", "Wendover", "PolyMatter", "Caspian Report", "Half as Interesting"],
    risk: "Hot topics (Russia, Israel, China-Taiwan) trigger limited monetization. Frame as explainer.",
  },
  {
    id: "ai",
    name: "AI from first principles",
    icon: Brain,
    iconColor: "text-violet-500",
    thesis: "Explain how AI actually works — substance, not hype",
    why: "Your strongest operating advantage — you live this. Two Minute Papers (1.74M), AI Explained (416K, premium audience).",
    rpm: "$8–12 / 1k views",
    topChannels: ["Two Minute Papers", "AI Explained", "Matt Wolfe", "Wes Roth", "Tina Huang"],
    risk: "Topic obsolescence — every model launch kills last week's video. Brutal competition.",
  },
];

const CATEGORY_PILL: Record<VideoCategory, string> = {
  Documentary: "bg-rose-500/15 text-rose-600 dark:text-rose-400",
  News: "bg-violet-500/15 text-violet-600 dark:text-violet-400",
  Format: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  Framework: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
};

function VideoRow({ video, onSelect }: { video: Video; onSelect: () => void }) {
  return (
    <button
      onClick={onSelect}
      className="w-full px-3 py-2 flex items-center gap-3 text-left hover:bg-muted/50 transition-colors border-b border-border/30 last:border-b-0"
    >
      <div className="w-12 h-12 rounded-md bg-muted overflow-hidden shrink-0 flex items-center justify-center">
        {video.posterSrc ? (
          <img src={video.posterSrc} alt="" className="w-full h-full object-cover" />
        ) : (
          <PlayCircle className="w-5 h-5 text-muted-foreground/60" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-foreground leading-snug line-clamp-2">{video.title}</p>
        <div className="flex items-center gap-1.5 mt-1">
          <span className={cn("px-1.5 py-0 rounded text-[10px] font-semibold", CATEGORY_PILL[video.category])}>
            {video.category}
          </span>
          <span className="text-[10px] text-muted-foreground tabular-nums">{video.duration}</span>
        </div>
      </div>
    </button>
  );
}

function VideoModal({ video, open, onOpenChange }: { video: Video | null; open: boolean; onOpenChange: (v: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90svh] overflow-y-auto">
        {video && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={cn("px-2 py-0.5 rounded-full text-[11px] font-semibold", CATEGORY_PILL[video.category])}>
                  {video.category}
                </span>
                <span className="text-xs text-muted-foreground tabular-nums">{video.duration}</span>
              </div>
              <DialogTitle className="text-base mt-1.5">{video.title}</DialogTitle>
              {video.topic && <DialogDescription className="text-xs">{video.topic}</DialogDescription>}
            </DialogHeader>
            <div className="aspect-video bg-black rounded-md overflow-hidden">
              <video
                key={video.id}
                controls
                autoPlay
                preload="metadata"
                poster={video.posterSrc}
                className="w-full h-full object-contain"
                src={video.videoSrc}
              />
            </div>
            {video.meta && video.meta.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-1">
                {video.meta.map((m) => (
                  <div key={m.label} className="border-t border-border/40 pt-2">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground/60">{m.label}</div>
                    <div className="text-xs font-medium text-foreground mt-0.5">{m.value}</div>
                  </div>
                ))}
              </div>
            )}
            {video.notes && (
              <p className="text-xs text-muted-foreground leading-relaxed border-t border-border/40 pt-3">{video.notes}</p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function NicheModal({ niche, open, onOpenChange }: { niche: NicheCandidate | null; open: boolean; onOpenChange: (v: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        {niche && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2.5">
                <niche.icon className={cn("w-5 h-5", niche.iconColor)} />
                <DialogTitle className="text-base">{niche.name}</DialogTitle>
              </div>
              <DialogDescription className="text-xs italic">{niche.thesis}</DialogDescription>
            </DialogHeader>
            <p className="text-sm text-foreground leading-relaxed">{niche.why}</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md bg-muted/50 p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground/60 mb-1">RPM</div>
                <div className="text-sm font-semibold text-foreground">{niche.rpm}</div>
              </div>
              <div className="rounded-md bg-muted/50 p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground/60 mb-1">Channels analyzed</div>
                <div className="text-sm font-semibold text-foreground">{niche.topChannels.length}</div>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground/60 mb-1.5">Top channels</div>
              <div className="flex flex-wrap gap-1.5">
                {niche.topChannels.map((c) => (
                  <span key={c} className="px-2 py-0.5 rounded-md bg-muted text-xs font-medium text-foreground/80">
                    {c}
                  </span>
                ))}
              </div>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-semibold text-amber-700 dark:text-amber-400">Risk · </span>
              {niche.risk}
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const TOTAL_RUNTIME_MIN = 67; // ~SVB doc + 10 news + 9 short demos
const TOTAL_VIDEOS = VIDEOS.length;
const TOTAL_COST = "~$5";

export function YouTubeLabPage() {
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [selectedNiche, setSelectedNiche] = useState<NicheCandidate | null>(null);
  const [filter, setFilter] = useState<VideoCategory | "All">("All");
  const [search, setSearch] = useState("");

  const filtered = VIDEOS.filter(
    (v) =>
      (filter === "All" || v.category === filter) &&
      (search === "" || v.title.toLowerCase().includes(search.toLowerCase()) || (v.topic ?? "").toLowerCase().includes(search.toLowerCase())),
  );

  const featured = VIDEOS.find((v) => v.id === "transformer-doc")!;

  return (
    <div className="-mx-6 -my-8 h-svh flex flex-col bg-background">
      {/* Compact header */}
      <header className="px-5 py-2.5 border-b bg-card flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-rose-500/15">
            <Youtube className="w-4 h-4 text-rose-500 fill-rose-500/20" />
          </div>
          <h1 className="text-sm font-semibold text-foreground">YouTube Content Lab</h1>
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-500/15 text-rose-600 dark:text-rose-400">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
            Active
          </span>
        </div>
        <div className="flex items-center gap-4 text-[11px] text-muted-foreground tabular-nums">
          <span>
            <strong className="text-foreground">{TOTAL_VIDEOS}</strong> videos
          </span>
          <span>
            <strong className="text-foreground">~{TOTAL_RUNTIME_MIN} min</strong> runtime
          </span>
          <span>
            <strong className="text-foreground">{TOTAL_COST}</strong> total cost
          </span>
        </div>
      </header>

      {/* 2-pane body */}
      <div className="flex flex-1 min-h-0">
        {/* LEFT — video library */}
        <aside className="w-72 shrink-0 border-r bg-card flex flex-col">
          <div className="px-3 py-2.5 border-b shrink-0">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-muted-foreground/60 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search videos…"
                className="w-full pl-7 pr-2 py-1.5 rounded-md bg-muted/50 border border-border/50 text-xs placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-rose-500/50"
              />
            </div>
            <div className="flex gap-1 mt-2 flex-wrap">
              {(["All", "News", "Documentary", "Framework", "Format"] as const).map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  className={cn(
                    "px-2 py-0.5 rounded text-[10px] font-semibold transition-colors",
                    filter === c
                      ? "bg-foreground text-background"
                      : "bg-muted/50 text-muted-foreground hover:bg-muted",
                  )}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">No videos match.</div>
            ) : (
              filtered.map((v) => <VideoRow key={v.id} video={v} onSelect={() => setSelectedVideo(v)} />)
            )}
          </div>
        </aside>

        {/* RIGHT — overview */}
        <main className="flex-1 overflow-y-auto p-4">
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-4">
            <KPI icon={Layers} label="Per video" value="$1–6" sub="research-backed" />
            <KPI icon={Radio} label="Cadence" value="4 / week" sub="AI-policy safe ceiling" />
            <KPI icon={TrendingUp} label="Monthly tooling" value="$25–110" sub="all-in 16 vids/mo" />
            <KPI icon={Wallet} label="Break-even" value="1 video" sub="1 finance hit pays the year" />
          </div>

          {/* Featured documentary */}
          <div className="rounded-xl border bg-card overflow-hidden mb-4">
            <button
              onClick={() => setSelectedVideo(featured)}
              className="block w-full text-left group"
            >
              <div className="relative aspect-video bg-black overflow-hidden">
                {featured.posterSrc && (
                  <img src={featured.posterSrc} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center">
                      <PlayCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-white/70">Featured · Documentary</div>
                      <div className="text-sm font-semibold text-white">{featured.title}</div>
                    </div>
                  </div>
                  <span className="text-[11px] text-white/80 tabular-nums shrink-0">{featured.duration}</span>
                </div>
              </div>
            </button>
          </div>

          {/* Niches */}
          <SectionLabel icon={Compass} label="Niche candidates · click to expand" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 mb-4">
            {NICHES.map((n) => (
              <button
                key={n.id}
                onClick={() => setSelectedNiche(n)}
                className="rounded-lg border bg-card p-3 text-left hover:border-rose-500/40 hover:bg-rose-500/5 transition-colors group"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <n.icon className={cn("w-4 h-4", n.iconColor)} />
                  <h3 className="text-xs font-semibold text-foreground">{n.name}</h3>
                </div>
                <p className="text-[11px] text-muted-foreground italic leading-snug mb-2 line-clamp-2">{n.thesis}</p>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60">RPM</span>
                  <span className="text-xs font-semibold text-foreground tabular-nums">{n.rpm}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Decision points + Survival rules side-by-side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 mb-2">
            <div className="rounded-lg border bg-card p-3">
              <SectionLabel icon={Sparkles} label="Decision points" small />
              <ol className="text-[11px] text-foreground/90 leading-relaxed flex flex-col gap-1.5 mt-2">
                <li className="flex gap-2"><span className="text-muted-foreground/60">1.</span> Pick the niche · Finance · World · AI</li>
                <li className="flex gap-2"><span className="text-muted-foreground/60">2.</span> Pick the format · Documentary leading the pack</li>
                <li className="flex gap-2"><span className="text-muted-foreground/60">3.</span> Lock the loop · cadence + cost ceiling</li>
                <li className="flex gap-2"><span className="text-muted-foreground/60">4.</span> Ship video #1 · iterate based on watch time</li>
              </ol>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50/40 dark:bg-amber-950/15 dark:border-amber-900/60 p-3">
              <SectionLabel icon={ShieldAlert} label="Survival rules · Jan 2026 AI policy" small />
              <div className="grid grid-cols-2 gap-2 mt-2 text-[11px]">
                <div>
                  <div className="text-emerald-700 dark:text-emerald-400 font-semibold mb-1">✓ Allowed</div>
                  <ul className="text-foreground/80 leading-snug flex flex-col gap-0.5">
                    <li>· Editorial pass / script</li>
                    <li>· Licensed AI voice</li>
                    <li>· 2–5 weekly · varied</li>
                    <li>· AI disclosure</li>
                  </ul>
                </div>
                <div>
                  <div className="text-rose-700 dark:text-rose-400 font-semibold mb-1">✗ Banned</div>
                  <ul className="text-foreground/80 leading-snug flex flex-col gap-0.5">
                    <li>· Unedited AI scripts</li>
                    <li>· Generic robovoice</li>
                    <li>· Identical templates</li>
                    <li>· 3+ uploads/day</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Modals */}
      <VideoModal video={selectedVideo} open={!!selectedVideo} onOpenChange={(v) => !v && setSelectedVideo(null)} />
      <NicheModal niche={selectedNiche} open={!!selectedNiche} onOpenChange={(v) => !v && setSelectedNiche(null)} />
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub }: { icon: typeof Layers; label: string; value: string; sub: string }) {
  return (
    <div className="rounded-lg border bg-card p-2.5">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3 h-3 text-muted-foreground/70" />
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60">{label}</span>
      </div>
      <div className="text-base font-bold text-foreground leading-tight">{value}</div>
      <div className="text-[10px] text-muted-foreground/80 mt-0.5">{sub}</div>
    </div>
  );
}

function SectionLabel({ icon: Icon, label, small = false }: { icon: typeof Sparkles; label: string; small?: boolean }) {
  return (
    <div className={cn("flex items-center gap-1.5", small ? "" : "mb-2")}>
      <Icon className={cn("text-rose-500", small ? "w-3 h-3" : "w-3.5 h-3.5")} />
      <span className={cn("font-semibold uppercase tracking-wider text-muted-foreground/80", small ? "text-[10px]" : "text-[11px]")}>
        {label}
      </span>
    </div>
  );
}

